import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CalendarIcon } from './Icons';
import {
  datePlaceholder,
  formatDateInput,
  maskDateInput,
  parseCivilDate,
  parseDateInput,
  resolveLanguage,
  resolveLocale,
  toDateInputValue,
  todayInputValue,
} from '../../utils/dateUtils';
import styles from './DateField.module.css';

const WEEK_DAYS = {
  'pt-BR': ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sab'],
  en: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
  es: ['Dom', 'Lun', 'Mar', 'Mie', 'Jue', 'Vie', 'Sab'],
};

export const buildCalendarDays = (monthDate) => {
  const first = new Date(monthDate.getFullYear(), monthDate.getMonth(), 1, 12, 0, 0);
  const last = new Date(monthDate.getFullYear(), monthDate.getMonth() + 1, 0, 12, 0, 0);
  const days = [];
  for (let index = 0; index < first.getDay(); index += 1) days.push(null);
  for (let day = 1; day <= last.getDate(); day += 1) {
    days.push(new Date(monthDate.getFullYear(), monthDate.getMonth(), day, 12, 0, 0));
  }
  while (days.length % 7 !== 0) days.push(null);
  return days;
};

const DateField = ({
  label,
  value,
  onChange,
  error,
  helpText,
  allowClear = false,
  min,
  max,
  required = false,
  disabled = false,
  className = '',
  name,
}) => {
  const { t, i18n } = useTranslation();
  const generatedId = useId();
  const inputId = name || generatedId;
  const rootRef = useRef(null);
  const selectedDate = parseCivilDate(value) || new Date();
  const [open, setOpen] = useState(false);
  const [text, setText] = useState(() => formatDateInput(value, i18n.language));
  const [month, setMonth] = useState(
    () => new Date(selectedDate.getFullYear(), selectedDate.getMonth(), 1, 12, 0, 0)
  );

  useEffect(() => {
    setText(formatDateInput(value, i18n.language));
    const next = parseCivilDate(value);
    if (next) setMonth(new Date(next.getFullYear(), next.getMonth(), 1, 12, 0, 0));
  }, [value, i18n.language]);

  useEffect(() => {
    if (!open) return undefined;
    const handlePointerDown = (event) => {
      if (!rootRef.current?.contains(event.target)) setOpen(false);
    };
    document.addEventListener('pointerdown', handlePointerDown);
    return () => document.removeEventListener('pointerdown', handlePointerDown);
  }, [open]);

  const days = useMemo(() => buildCalendarDays(month), [month]);
  const language = resolveLanguage(i18n.language);
  const normalizedMin = toDateInputValue(min);
  const normalizedMax = toDateInputValue(max);

  const handleTextChange = (event) => {
    const masked = maskDateInput(event.target.value);
    setText(masked);
    if (!masked) {
      onChange('');
      return;
    }
    if (masked.length === 10) {
      const parsed = parseDateInput(masked, i18n.language);
      if (parsed) onChange(parsed);
    }
  };

  const handleBlur = () => {
    if (!text) return;
    const parsed = parseDateInput(text, i18n.language);
    if (parsed) {
      onChange(parsed);
      setText(formatDateInput(parsed, i18n.language));
    } else {
      onChange('');
    }
  };

  const selectDate = (date) => {
    const next = toDateInputValue(date);
    onChange(next);
    setText(formatDateInput(next, i18n.language));
    setOpen(false);
  };

  return (
    <div ref={rootRef} className={`${styles.field} ${className}`}>
      {label ? <label className={styles.label} htmlFor={inputId}>{label}</label> : null}
      <div className={styles.inputWrap}>
        <input
          id={inputId}
          name={name}
          className={`${styles.input} ${error ? styles.inputError : ''}`}
          value={text}
          onChange={handleTextChange}
          onBlur={handleBlur}
          placeholder={datePlaceholder(i18n.language)}
          inputMode="numeric"
          autoComplete="off"
          maxLength={10}
          required={required}
          disabled={disabled}
        />
        <div className={styles.inputActions}>
          {allowClear && value && !disabled ? (
            <button
              className={styles.clearButton}
              type="button"
              onClick={() => onChange('')}
              aria-label={t('common.clearDate')}
              title={t('common.clearDate')}
            >
              x
            </button>
          ) : null}
          <button
            className={styles.calendarButton}
            type="button"
            onClick={() => setOpen((current) => !current)}
            disabled={disabled}
            aria-label={t('common.openCalendar')}
            title={t('common.openCalendar')}
          >
            <CalendarIcon className={styles.calendarIcon} />
          </button>
        </div>
      </div>
      {error ? <span className={styles.error}>{error}</span> : null}
      {helpText && !error ? <span className={styles.help}>{helpText}</span> : null}

      {open && !disabled ? (
        <div className={styles.popover}>
          <div className={styles.calendarHeader}>
            <button type="button" className={styles.navButton} onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() - 1, 1, 12, 0, 0))} aria-label="Previous month">
              {'<'}
            </button>
            <strong className={styles.monthTitle}>
              {month.toLocaleDateString(resolveLocale(i18n.language), { month: 'long', year: 'numeric' })}
            </strong>
            <button type="button" className={styles.navButton} onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() + 1, 1, 12, 0, 0))} aria-label="Next month">
              {'>'}
            </button>
          </div>
          <div className={styles.weekRow}>
            {WEEK_DAYS[language].map((day) => <span key={day}>{day}</span>)}
          </div>
          <div className={styles.daysGrid}>
            {days.map((date, index) => {
              const dateValue = date ? toDateInputValue(date) : '';
              const disabled = Boolean(
                dateValue &&
                ((normalizedMin && dateValue < normalizedMin) || (normalizedMax && dateValue > normalizedMax))
              );
              return date ? (
                <button
                  key={dateValue}
                  type="button"
                  disabled={disabled}
                  className={[
                    styles.day,
                    dateValue === value ? styles.selectedDay : '',
                    dateValue === todayInputValue() ? styles.today : '',
                  ].join(' ')}
                  onClick={() => selectDate(date)}
                >
                  {date.getDate()}
                </button>
              ) : <span key={`empty-${index}`} className={styles.emptyDay} />;
            })}
          </div>
          <div className={styles.calendarFooter}>
            {allowClear ? (
              <button type="button" className={styles.textButton} onClick={() => { onChange(''); setOpen(false); }}>
                {t('common.clearDate')}
              </button>
            ) : <span />}
            <button type="button" className={styles.todayButton} onClick={() => selectDate(new Date())}>
              {t('common.today')}
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default DateField;
